/**
 * Currently notifications are only reporting on customer data.
 */
import {
  NOTIFICATION_TYPE,
  NOTIFICATION_ACTION,
  Customer,
  CustomerRequest,
  CustomerResponse,
  CustomerResponseSuccess,
  CustomerResponseError,
  CustomerResponsePending
} from "../types";
import { hasAndIsNotEmpty, get } from "./helpers";
import NotificationsStore, { Notification } from "./NotificationsStore";
import { getTitleLabelKey } from "./titles";

export const notificationsStore = new NotificationsStore();

export function addSuccessNotification(
  customerRes: CustomerResponseSuccess
): Notification | undefined {
  const actionType = get(customerRes, "data.type");
  let action = "";
  switch (actionType) {
    case "CUSTOMER_DATA_CREATED":
      action = NOTIFICATION_ACTION.CUSTOMER_DATA_CREATED;
      break;
    case "CUSTOMER_DATA_UPDATED":
      action = NOTIFICATION_ACTION.CUSTOMER_DATA_UPDATED;
      break;
    default:
      action = NOTIFICATION_ACTION.CUSTOMER_DATA_CREATED;
      break;
  }
  return notificationsStore.add({
    ...getNotificationParams(customerRes),
    type: NOTIFICATION_TYPE.SUCCESS,
    action,
    aggregateId: customerRes.data.identifier || customerRes.requestId
  });
}

export function addPendingNotification(
  customerReq: CustomerRequest,
  customerRes: CustomerResponsePending
): Notification | undefined {
  return notificationsStore.add({
    ...getNotificationParams(customerRes),
    type: NOTIFICATION_TYPE.PENDING,
    action: NOTIFICATION_ACTION.CUSTOMER_DATA_PENDING,
    aggregateId: customerRes.hashcode || customerReq.requestId
  });
}

export function addErrorNotification(
  customerReq: CustomerRequest,
  customerRes: CustomerResponseError
): Notification | undefined {
  return notificationsStore.add({
    type: NOTIFICATION_TYPE.ERROR,
    action:
      customerRes.errorType === "failure"
        ? NOTIFICATION_ACTION.CUSTOMER_DATA_FAILURE
        : NOTIFICATION_ACTION.CUSTOMER_DATA_API_ERROR,
    aggregateId: customerReq.requestId,
    salesPersonId: customerReq.salesPersonId,
    translationId: customerRes.translationId,
    params: {
      attempt: customerRes.attempt,
      errorCode: customerRes.errorCode || "???",
      errorMessage: customerRes.errorMessage || "Unknown error occurred",
      traceToken: customerRes.traceToken
    }
  });
}

export function addCustomerNotFoundNotification(
  customerId: string
): Notification | undefined {
  return notificationsStore.add({
    type: NOTIFICATION_TYPE.ERROR,
    action: NOTIFICATION_ACTION.CUSTOMER_NOT_FOUND,
    aggregateId: new Date().valueOf(),
    salesPersonId: "",
    translationId: "",
    params: {
      customerId: customerId
    }
  });
}

export function addNotification(
  customerReq: CustomerRequest,
  customerRes: CustomerResponse
): Notification | undefined {
  if (hasAndIsNotEmpty(customerRes, "errorType")) {
    return addErrorNotification(
      customerReq,
      customerRes as CustomerResponseError
    );
  }
  if (hasAndIsNotEmpty(customerRes, "hashcode")) {
    return addPendingNotification(
      customerReq,
      customerRes as CustomerResponsePending
    );
  }
  return addSuccessNotification(customerRes as CustomerResponseSuccess);
}

// @NOTE please only enable this on your local version. Since we can create customers
// via the API now, we don't need to generate notifications, unless debugging something
// with the notifications page.
// if (process.env.NODE_ENV === "development" && notificationsStore.length === 0) {
//   import("./fakeData").then(fakeDataModule => {
//     const { genFakeNotifications } = fakeDataModule;
//     if (confirm("Want to generate some fake notifications?")) {
//       genFakeNotifications();
//     }
//   });
// }

function getNotificationParams(customerRes: CustomerResponseSuccess) {
  const customer: Customer = customerRes.data;
  return {
    salesPersonId: customerRes.salesPersonId,
    params: {
      // prevent translation to crash if {honorificPrefix} is in the body,
      // and there is no honorificPrefix.
      honorificPrefix: customer.title || "",
      honorificPrefixLabelKey: getTitleLabelKey(customer.title),
      givenName: customer.firstName,
      familyName: customer.lastName,
      title: customer.title || "",
      firstName: customer.firstName,
      lastName: customer.lastName,
      customerId: customer.identifier,
      traceToken: customerRes.traceToken
    }
  };
}

export default notificationsStore;
